export interface IdentityMatchResult {
  score: number; // 0 to 100
  isExactMatch: boolean; // true if exact match or official multi-firstname match eligible for auto-validation
  firstNameScore: number; // 0 to 100
  lastNameScore: number; // 0 to 100
  level: 'EXACT' | 'HIGH' | 'MEDIUM' | 'LOW';
}

export class IdentityMatchUtils {
  /**
   * Normalizes a string: lowercase, strips accents and non-alphanumeric characters.
   */
  static normalize(str: string): string {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '')
      .trim();
  }

  /**
   * Splits a name into individual normalized tokens (words).
   * e.g. "Jean-Pierre Martin" -> ["jean", "pierre", "martin"]
   */
  static normalizeWords(str: string): string[] {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .split(/[\s\-',._]+/)
      .map((w) => w.replace(/[^a-z0-9]/g, '').trim())
      .filter((w) => w.length > 0);
  }

  /**
   * Computes the Jaro distance between two strings (0.0 to 1.0).
   */
  static jaroDistance(s1: string, s2: string): number {
    if (s1 === s2) return 1.0;
    if (!s1 || !s2) return 0.0;

    const len1 = s1.length;
    const len2 = s2.length;
    const matchDistance = Math.floor(Math.max(len1, len2) / 2) - 1;

    const s1Matches = new Array(len1).fill(false);
    const s2Matches = new Array(len2).fill(false);

    let matches = 0;
    for (let i = 0; i < len1; i++) {
      const start = Math.max(0, i - matchDistance);
      const end = Math.min(i + matchDistance + 1, len2);

      for (let j = start; j < end; j++) {
        if (!s2Matches[j] && s1[i] === s2[j]) {
          s1Matches[i] = true;
          s2Matches[j] = true;
          matches++;
          break;
        }
      }
    }

    if (matches === 0) return 0.0;

    let k = 0;
    let transpositions = 0;
    for (let i = 0; i < len1; i++) {
      if (s1Matches[i]) {
        while (!s2Matches[k]) {
          k++;
        }
        if (s1[i] !== s2[k]) {
          transpositions++;
        }
        k++;
      }
    }

    const m = matches;
    return (m / len1 + m / len2 + (m - transpositions / 2) / m) / 3;
  }

  /**
   * Computes the Jaro-Winkler similarity between two strings (0.0 to 1.0).
   * Favors strings with common prefixes, ideal for personal names.
   */
  static jaroWinkler(s1: string, s2: string): number {
    const jaro = this.jaroDistance(s1, s2);
    if (jaro === 0.0) return 0.0;

    // Common prefix length up to 4 characters
    let prefix = 0;
    const maxPrefix = Math.min(4, Math.min(s1.length, s2.length));
    for (let i = 0; i < maxPrefix; i++) {
      if (s1[i] === s2[i]) {
        prefix++;
      } else {
        break;
      }
    }

    const scalingFactor = 0.1;
    return jaro + prefix * scalingFactor * (1 - jaro);
  }

  /**
   * Computes matching score for first names (0 to 1).
   * Handles multi-prenoms on official documents (e.g. CNI "Robin Pierre" vs profil "Robin").
   */
  static compareFirstNames(stripeFirst: string, userFirst: string): number {
    const normStripe = this.normalize(stripeFirst);
    const normUser = this.normalize(userFirst);

    if (!normStripe || !normUser) return 0;
    if (normStripe === normUser) return 1.0;

    const stripeWords = this.normalizeWords(stripeFirst);
    const userWords = this.normalizeWords(userFirst);

    // If user's first name matches ANY of the Stripe document's first names exactly: 100%
    // e.g. Profil: "Robin", Document: "Robin Jacques Pierre"
    if (userWords.length > 0 && userWords.every((uw) => stripeWords.includes(uw))) {
      return 1.0;
    }

    // Inverse: Document: "Robin", Profil: "Robin Jacques"
    if (stripeWords.length > 0 && stripeWords.every((sw) => userWords.includes(sw))) {
      return 0.95;
    }

    // Compare individual tokens using Jaro-Winkler
    let totalScore = 0;
    for (const uw of userWords) {
      let maxWordScore = 0;
      for (const sw of stripeWords) {
        const jw = this.jaroWinkler(uw, sw);
        if (jw > maxWordScore) maxWordScore = jw;
      }
      totalScore += maxWordScore;
    }

    const tokenScore = userWords.length > 0 ? totalScore / userWords.length : 0;
    const fullScore = this.jaroWinkler(normStripe, normUser);

    return Math.max(tokenScore, fullScore);
  }

  /**
   * Computes matching score for last names (0 to 1).
   * Handles compound names (e.g. "Martin-Dubois" vs "Dubois").
   */
  static compareLastNames(stripeLast: string, userLast: string): number {
    const normStripe = this.normalize(stripeLast);
    const normUser = this.normalize(userLast);

    if (!normStripe || !normUser) return 0;
    if (normStripe === normUser) return 1.0;

    const fullScore = this.jaroWinkler(normStripe, normUser);

    const stripeWords = this.normalizeWords(stripeLast);
    const userWords = this.normalizeWords(userLast);

    // Compound name match (e.g. "Dubois" included in "Martin-Dubois")
    if (userWords.length > 0 && userWords.every((uw) => stripeWords.includes(uw))) {
      return 0.92;
    }
    if (stripeWords.length > 0 && stripeWords.every((sw) => userWords.includes(sw))) {
      return 0.92;
    }

    return fullScore;
  }

  /**
   * Computes full identity match score and details between Stripe verified data and user profile.
   */
  static calculateMatch(
    stripeFirst: string,
    stripeLast: string,
    userFirst: string,
    userLast: string,
  ): IdentityMatchResult {
    const firstScore = this.compareFirstNames(stripeFirst, userFirst);
    const lastScore = this.compareLastNames(stripeLast, userLast);

    const firstNamePercent = Math.round(firstScore * 100);
    const lastNamePercent = Math.round(lastScore * 100);

    // 50% first name, 50% last name
    const overallScore = Math.round((firstNamePercent + lastNamePercent) / 2);

    // Exact match qualifies for auto-verification without manual admin review
    const isExactMatch = firstScore >= 0.99 && lastScore >= 0.99;

    let level: 'EXACT' | 'HIGH' | 'MEDIUM' | 'LOW';
    if (isExactMatch) {
      level = 'EXACT';
    } else if (overallScore >= 85) {
      level = 'HIGH';
    } else if (overallScore >= 70) {
      level = 'MEDIUM';
    } else {
      level = 'LOW';
    }

    return {
      score: overallScore,
      isExactMatch,
      firstNameScore: firstNamePercent,
      lastNameScore: lastNamePercent,
      level,
    };
  }
}
